import React, { useEffect, useState } from 'react';
import { FilterDrawer, FilterFormSection } from '../ui/FilterDrawer';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';

export const DEFAULT_STOCK_LIST_FILTERS = {
  stockTab: 'all',
  query: '',
  sort: 'stocked_desc',
};

const SORT_OPTIONS = [
  { value: 'stocked_desc', label: 'Stocked: newest first' },
  { value: 'stocked_asc', label: 'Stocked: oldest first (FIFO)' },
  { value: 'party_asc', label: 'Party A–Z' },
  { value: 'pieces_desc', label: 'Quantity: high to low' },
  { value: 'order_code_asc', label: 'Order # A–Z' },
];

const TABS = [
  { id: 'all', label: 'All', sub: 'Everything in stock' },
  { id: 'ready', label: 'Ready to ship', sub: 'Not on hold' },
  { id: 'hold', label: 'On hold', sub: 'Blocked for dispatch' },
];

const searchIcon = (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

/**
 * Stock list: ship view, text search, sort. Right-hand drawer, same pattern as orders.
 */
export function StockFiltersModal({ open, onClose, value, onApply, tabCounts }) {
  const [draft, setDraft] = useState(() => ({ ...DEFAULT_STOCK_LIST_FILTERS }));

  useEffect(() => {
    if (!open) return;
    setDraft({ ...DEFAULT_STOCK_LIST_FILTERS, ...value });
  }, [open, value]);

  const clear = () => {
    setDraft({ ...DEFAULT_STOCK_LIST_FILTERS });
  };

  const apply = () => {
    onApply(draft);
    onClose();
  };

  const draftCount = countActiveStockFilters(draft);
  const tc = tabCounts || { all: 0, ready: 0, hold: 0 };
  const tabCount = (id) => (id === 'all' ? tc.all : id === 'ready' ? tc.ready : tc.hold);

  return (
    <FilterDrawer
      open={open}
      onClose={onClose}
      width="lg"
      titleId="stock-filters-title"
      eyebrow="Warehouse"
      title="In-stock list"
      description={
        draftCount === 0
          ? 'Choose who to focus on, search, and sort. Tap Show results when ready.'
          : `${draftCount} ${draftCount === 1 ? 'criterion' : 'criteria'} selected — Show results to apply.`
      }
      headerActions={
        <button
          type="button"
          onClick={clear}
          className="rounded-lg py-1.5 px-2.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
        >
          Reset all
        </button>
      }
      footer={(
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end sm:gap-3">
          <Button type="button" variant="secondary" className="w-full sm:w-auto" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" className="w-full min-w-[9rem] sm:w-auto" onClick={apply}>
            Show results
          </Button>
        </div>
      )}
    >
      <div className="space-y-5 sm:space-y-6">
        <FilterFormSection
          title="Dispatch readiness"
          hint="Use counts as a quick sanity check before you ship or release holds."
        >
          <div
            className="grid grid-cols-1 gap-2.5 sm:grid-cols-3 sm:gap-3"
            role="listbox"
            aria-label="Ship readiness"
          >
            {TABS.map((t) => {
              const selected = draft.stockTab === t.id;
              const count = tabCount(t.id);
              return (
                <div key={t.id} className="min-w-0" role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    onClick={() => setDraft((d) => ({ ...d, stockTab: t.id }))}
                    className={`
                      flex w-full min-h-[5.5rem] flex-col rounded-2xl border-2 p-3.5 text-left transition
                      sm:min-h-[5.25rem] sm:justify-center
                      ${
                        selected
                          ? 'border-primary-500 bg-primary-50/60 shadow-sm ring-2 ring-primary-500/15'
                          : 'border-slate-200/90 bg-white hover:border-slate-300 hover:bg-slate-50/60'
                      }
                    `.trim()}
                  >
                    <span
                      className={`text-sm font-bold leading-tight ${selected ? 'text-primary-900' : 'text-slate-800'}`}
                    >
                      {t.label}
                    </span>
                    <span
                      className={`mt-0.5 text-[0.7rem] font-medium leading-snug ${selected ? 'text-primary-800/80' : 'text-slate-500'}`}
                    >
                      {t.sub}
                    </span>
                    <span
                      className={`
                        mt-2.5 text-[0.7rem] font-bold tabular-nums uppercase tracking-wide
                        ${selected ? 'text-primary-700' : 'text-slate-400'}
                      `}
                    >
                      {count} {count === 1 ? 'order' : 'orders'}
                    </span>
                  </button>
                </div>
              );
            })}
          </div>
        </FilterFormSection>

        <FilterFormSection
          title="Narrow the rows"
          hint="Matches order #, party, design, address, and remarks."
        >
          <Input
            type="search"
            leftIcon={searchIcon}
            label="Search in stock"
            placeholder="Order #, party, phone, design, note…"
            value={draft.query}
            onChange={(e) => setDraft((d) => ({ ...d, query: e.target.value }))}
          />
        </FilterFormSection>

        <FilterFormSection title="Order of rows" hint="Defaults to most recently stocked first.">
          <div className="max-w-md">
            <Select
              label="Sort by"
              value={draft.sort}
              onChange={(e) => setDraft((d) => ({ ...d, sort: e.target.value }))}
              options={SORT_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            />
          </div>
        </FilterFormSection>
      </div>
    </FilterDrawer>
  );
}

export function countActiveStockFilters(f) {
  const d = DEFAULT_STOCK_LIST_FILTERS;
  let n = 0;
  if (f.stockTab && f.stockTab !== d.stockTab) n += 1;
  if (String(f.query || '').trim()) n += 1;
  if (f.sort && f.sort !== d.sort) n += 1;
  return n;
}

export default StockFiltersModal;
